package com.example.mystybag.core.logic.base

import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.ExperimentalDecomposeApi
import com.arkivanov.decompose.router.children.ChildNavState
import com.arkivanov.decompose.router.children.NavigationSource
import com.arkivanov.decompose.router.pages.ChildPages
import com.arkivanov.decompose.router.pages.Pages
import com.arkivanov.decompose.router.pages.PagesNavigation
import com.arkivanov.decompose.router.pages.childPages
import com.arkivanov.decompose.router.slot.ChildSlot
import com.arkivanov.decompose.router.slot.SlotNavigation
import com.arkivanov.decompose.router.slot.childSlot
import com.arkivanov.decompose.router.stack.ChildStack
import com.arkivanov.decompose.router.stack.StackNavigation
import com.arkivanov.decompose.router.stack.childStack
import com.arkivanov.decompose.value.Value
import com.example.mystybag.core.helpers.ProgressListener
import com.example.mystybag.core.model.SnackbarConfig

interface ParentCallback {
    fun parentCloseBottomSheet() {}
    fun parentOpenBottomSheet(config: UIBottomSheetConfig)
    fun goBack(onComplete: ((Boolean) -> Unit)? = null)
}

class AppContext (
    context                 : ComponentContext,
    val callbacks           : ParentCallback? = null,
    val progressListener    : ProgressListener? = null,
    val navigation          : StackNavigation<NavigatorConfig>? = null
): ComponentContext by context

fun AppContext.appChildStack(
    source                  : NavigationSource<StackNavigation.Event<NavigatorConfig>>,
    initialConfiguration    : NavigatorConfig,
    key                     : () -> String,
    navigation              : () -> StackNavigation<NavigatorConfig>,
    handleBackButton        : Boolean = false,
    callbacks               : ((NavigatorConfig) -> ParentCallback?)? = null,
    progressListener        : ((NavigatorConfig) -> ProgressListener?)? = null,
    childFactory            : (configuration: NavigatorConfig, AppContext) -> BaseViewModel
): Value<ChildStack<NavigatorConfig, BaseViewModel>> {
    return childStack(
        source              = source,
        initialStack        = { listOf(initialConfiguration) },
        serializer          = null,
        key                 = key.invoke(),
        handleBackButton    = handleBackButton
    ) { configuration, context ->

        childFactory(
            configuration,
            AppContext(
                context          = context,
                callbacks        = callbacks?.invoke(configuration),
                progressListener = progressListener?.invoke(configuration),
                navigation       = navigation()
            )
        )
    }
}

@ExperimentalDecomposeApi
fun AppContext.appChildPages(
    source              : NavigationSource<PagesNavigation.Event<NavigatorConfig>>,
    initialPages        : () -> Pages<NavigatorConfig>,
    key                 : String = "DefaultChildPages",
    navigation          : StackNavigation<NavigatorConfig>?,
    callbacks           : ((NavigatorConfig) -> ParentCallback?)? = null,
    progressListener    : ((NavigatorConfig) -> ProgressListener?)? = null,
    handleBackButton    : Boolean = false,
    pageStatus          : (index: Int, Pages<NavigatorConfig>) -> ChildNavState.Status = ::getDefaultPageStatus,
    childFactory        : (configuration: NavigatorConfig, AppContext) -> BaseViewModel,
): Value<ChildPages<NavigatorConfig, BaseViewModel>> =
    childPages(
        source              = source,
        initialPages        = initialPages,
        savePages           = { null },
        restorePages        = { null },
        key                 = key,
        pageStatus          = pageStatus,
        handleBackButton    = handleBackButton
    ) { configuration, context ->
        childFactory(
            configuration,
            AppContext(
                context          = context,
                callbacks        = callbacks?.invoke(configuration),
                progressListener = progressListener?.invoke(configuration),
                navigation       = navigation
            )
        )
    }

fun AppContext.bsChildSlot(
    source                  : NavigationSource<SlotNavigation.Event<UIBottomSheetConfig>>,
    key                     : String = "BottomSheetChildSlot",
    initialConfiguration    : () -> UIBottomSheetConfig? = { null },
    handleBackButton        : Boolean = false,
    navigation              : (() -> StackNavigation<NavigatorConfig>?)? = null,
    callback                : (() -> ParentCallback?)? = null,
    childFactory            : (configuration: UIBottomSheetConfig, AppContext) -> BottomSheetChild,
): Value<ChildSlot<UIBottomSheetConfig, BottomSheetChild>> =
    childSlot(
        source                  = source,
        saveConfiguration       = { null },
        restoreConfiguration    = { null },
        key                     = key,
        initialConfiguration    = initialConfiguration,
        handleBackButton        = handleBackButton
    ) { configuration, context ->
        childFactory(
            configuration,
            AppContext(
                context     = context,
                callbacks   = callback?.invoke(),
                navigation  = navigation?.invoke()
            )
        )
    }

fun AppContext.dialogChildSlot(
    source                  : NavigationSource<SlotNavigation.Event<DialogConfig>>,
    key                     : String = "DialogChildSlot",
    initialConfiguration    : () -> DialogConfig? = { null },
    handleBackButton        : Boolean = false,

): Value<ChildSlot<DialogConfig, DialogConfig>> =
    childSlot(
        source                  = source,
        saveConfiguration       = { null },
        restoreConfiguration    = { null },
        key                     = key,
        initialConfiguration    = initialConfiguration,
        handleBackButton        = handleBackButton
    ) { condif, _ -> condif }


fun AppContext.snackChildSlot(
    source                  : NavigationSource<SlotNavigation.Event<SnackbarConfig>>,
    key                     : String = "SnackBarChildSlot",
    initialConfiguration    : () -> SnackbarConfig? = { null },
    handleBackButton        : Boolean = false,
): Value<ChildSlot<SnackbarConfig, SnackbarConfig>> =
    childSlot(
        source                  = source,
        saveConfiguration       = { null },
        restoreConfiguration    = { null },
        key                     = key,
        initialConfiguration    = initialConfiguration,
        handleBackButton        = handleBackButton
    ) { config, _ -> config }

@PublishedApi
internal fun getDefaultPageStatus(index: Int, pages: Pages<*>): ChildNavState.Status =
    when (index) {
        pages.selectedIndex                                             -> ChildNavState.Status.RESUMED
        in (pages.selectedIndex - 1)..(pages.selectedIndex + 1)    -> ChildNavState.Status.CREATED
        else                                                            -> ChildNavState.Status.DESTROYED
    }

//ChildStack helpers
fun Value<ChildStack<*, BaseViewModel>>?.first(): ParentCallback? = this?.value?.items?.firstOrNull()?.instance
fun Value<ChildStack<*, BaseViewModel>>?.last() : ParentCallback? = this?.value?.items?.lastOrNull() ?.instance

inline fun<reified T> Value<ChildStack<*, BaseViewModel>>?.first(): T? = this?.value?.items?.firstOrNull { it.instance is T }?.instance as? T
inline fun<reified T> Value<ChildStack<*, BaseViewModel>>?.last() : T? = this?.value?.items?.lastOrNull  { it.instance is T }?.instance as? T
inline fun<reified T> Value<ChildStack<*, BaseViewModel>>?.forEach(block: (T) -> Unit) = this?.value?.items?.filter { it.instance is T }?.map { it.instance as T }?.forEach(block)
inline fun<reified T> Value<ChildStack<*, BaseViewModel>>?.indexOfFirst() = this?.value?.items?.indexOfFirst { it.instance is T } ?: 0
inline fun<reified T> Value<ChildStack<*, BaseViewModel>>?.indexOfLast() = this?.value?.items?.indexOfLast { it.instance is T } ?: 0

//ChildPages helpers
fun Value<ChildPages<*, BaseViewModel>>?.firstPage(): ParentCallback? = this?.value?.items?.firstOrNull()?.instance
fun Value<ChildPages<*, BaseViewModel>>?.lastPage() : ParentCallback? = this?.value?.items?.lastOrNull() ?.instance

inline fun<reified T> Value<ChildPages<*, BaseViewModel>>?.firstPage(): T? = this?.value?.items?.firstOrNull { it.instance is T }?.instance as? T
inline fun<reified T> Value<ChildPages<*, BaseViewModel>>?.lastPage() : T? = this?.value?.items?.lastOrNull  { it.instance is T }?.instance as? T
inline fun<reified T> Value<ChildPages<*, BaseViewModel>>?.activePage() : T? = this?.value?.items?.get(this.value.selectedIndex)?.instance as? T
inline fun<reified T> Value<ChildPages<*, BaseViewModel>>?.forEachPage(block: (T) -> Unit) = this?.value?.items?.filter { it.instance is T }?.map { it.instance as T }?.forEach(block)